// Throwaway prototype: current Library/Playlists drawer behavior with in-memory data.
import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Calendar,
  Check,
  CheckCircle,
  ChevronLeft,
  Clock,
  Eye,
  FolderOpen,
  GraduationCap,
  GripVertical,
  LibraryBig,
  List,
  ListVideo,
  MoreVertical,
  Play,
  Plus,
  Search,
  Share2,
  SlidersHorizontal,
  Trash2,
  User,
  Video,
  X,
  Youtube
} from 'lucide-react';
import './LibraryPrototype.css';

type LibrarySurface = 'library' | 'playlists';
type SourceFilter = 'all' | 'youtube' | 'uploaded';
type StatusFilter = 'all' | 'unassigned' | LearningStatus;
type LearningStatus = 'queued' | 'learning' | 'learned';
type ItemCollection = 'projects' | 'videos';
type SourceKind = 'youtube' | 'uploaded';

interface ProtoShare {
  accessCount: number;
  maxViews: number;
  daysRemaining: number;
}

interface ProtoLibraryItem {
  id: string;
  collection: ItemCollection;
  title: string;
  source: SourceKind;
  createdLabel: string;
  sizeLabel: string;
  segments: number;
  status?: LearningStatus;
  thumbnailClass: string;
  share?: ProtoShare;
}

interface ProtoPlaylist {
  id: string;
  title: string;
}

interface ProtoMembership {
  playlistId: string;
  itemId: string;
  itemCollection: ItemCollection;
  position: number;
}

interface ToastState {
  message: string;
  undo?: () => void;
}

const STATUS_SEQUENCE: Array<LearningStatus | undefined> = [
  undefined,
  'queued',
  'learning',
  'learned'
];

const INITIAL_ITEMS: ProtoLibraryItem[] = [
  {
    id: 'groove-upload',
    collection: 'projects',
    title: 'Groove set - counts cleaned',
    source: 'uploaded',
    createdLabel: 'Today',
    sizeLabel: '84.2 MB',
    segments: 14,
    status: 'learning',
    thumbnailClass: 'library-thumb-teal',
    share: { accessCount: 24, maxViews: 30, daysRemaining: 1 }
  },
  {
    id: 'yt-project',
    collection: 'projects',
    title: 'Heels class reference',
    source: 'youtube',
    createdLabel: 'Yesterday',
    sizeLabel: 'No upload storage',
    segments: 9,
    status: 'queued',
    thumbnailClass: 'library-thumb-red'
  },
  {
    id: 'floorwork-export',
    collection: 'videos',
    title: 'Floorwork transitions',
    source: 'uploaded',
    createdLabel: '4 days ago',
    sizeLabel: '121.8 MB',
    segments: 18,
    status: 'learned',
    thumbnailClass: 'library-thumb-violet'
  },
  {
    id: 'arms-study',
    collection: 'projects',
    title: 'Arms pathway study',
    source: 'uploaded',
    createdLabel: '2 weeks ago',
    sizeLabel: '62.6 MB',
    segments: 7,
    thumbnailClass: 'library-thumb-amber'
  }
];

const INITIAL_PLAYLISTS: ProtoPlaylist[] = [
  { id: 'playlist-groove', title: 'Groove Set' },
  { id: 'playlist-heels', title: 'Heels Set' },
  { id: 'playlist-empty', title: 'Empty Set' }
];

const INITIAL_MEMBERSHIPS: ProtoMembership[] = [
  { playlistId: 'playlist-groove', itemId: 'yt-project', itemCollection: 'projects', position: 0 },
  { playlistId: 'playlist-groove', itemId: 'groove-upload', itemCollection: 'projects', position: 1 },
  { playlistId: 'playlist-groove', itemId: 'floorwork-export', itemCollection: 'videos', position: 2 },
  { playlistId: 'playlist-heels', itemId: 'yt-project', itemCollection: 'projects', position: 0 }
];

const itemKey = (item: Pick<ProtoLibraryItem, 'id' | 'collection'>) => `${item.collection}:${item.id}`;
const membershipKey = (membership: ProtoMembership) => `${membership.itemCollection}:${membership.itemId}`;
const consumesUploadSlot = (item: ProtoLibraryItem) => item.source === 'uploaded';

const getStatusLabel = (status: LearningStatus | undefined) => status || 'unassigned';

const getNextStatus = (status: LearningStatus | undefined) => {
  const index = STATUS_SEQUENCE.indexOf(status);
  return STATUS_SEQUENCE[(index + 1) % STATUS_SEQUENCE.length];
};

const getStatusIcon = (status: LearningStatus | undefined) => {
  if (status === 'queued') return <List size={14} />;
  if (status === 'learning') return <GraduationCap size={15} />;
  if (status === 'learned') return <CheckCircle size={14} />;
  return null;
};

const sortMemberships = (memberships: ProtoMembership[]) =>
  [...memberships].sort((first, second) =>
    first.position - second.position || membershipKey(first).localeCompare(membershipKey(second))
  );

const slugify = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'playlist';

const LibraryPrototype: React.FC = () => {
  const [items, setItems] = useState(INITIAL_ITEMS);
  const [playlists, setPlaylists] = useState(INITIAL_PLAYLISTS);
  const [memberships, setMemberships] = useState(INITIAL_MEMBERSHIPS);
  const [activeSurface, setActiveSurface] = useState<LibrarySurface>('library');
  const [activePlaylistId, setActivePlaylistId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [openMenuKey, setOpenMenuKey] = useState<string | null>('projects:groove-upload');
  const [openPickerKey, setOpenPickerKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const [playlistQuery, setPlaylistQuery] = useState('');
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [createPlaylistOpen, setCreatePlaylistOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>({
    message: 'Added Groove set - counts cleaned to Groove Set'
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const activePlaylist = playlists.find(playlist => playlist.id === activePlaylistId) || null;
  const isPlaylistCollectionView = activeSurface === 'playlists' && !activePlaylistId;
  const isSearchExpanded = searchOpen || normalizedSearchQuery.length > 0;

  const itemsByKey = useMemo(
    () => new Map(items.map(item => [itemKey(item), item])),
    [items]
  );

  const currentPlaylistIdsByItem = useMemo(() => {
    const byItem = new Map<string, string[]>();
    memberships.forEach(membership => {
      const key = membershipKey(membership);
      byItem.set(key, [...(byItem.get(key) || []), membership.playlistId]);
    });
    return byItem;
  }, [memberships]);

  const playlistCoverItems = useMemo(() => {
    const covers = new Map<string, ProtoLibraryItem | null>();
    playlists.forEach(playlist => {
      const cover = sortMemberships(memberships.filter(membership => membership.playlistId === playlist.id))
        .map(membership => itemsByKey.get(membershipKey(membership)))
        .find((item): item is ProtoLibraryItem => Boolean(item)) || null;
      covers.set(playlist.id, cover);
    });
    return covers;
  }, [itemsByKey, memberships, playlists]);

  const orderedPlaylistItems = useMemo(() => {
    if (!activePlaylistId) return items;
    return sortMemberships(memberships.filter(membership => membership.playlistId === activePlaylistId))
      .map(membership => itemsByKey.get(membershipKey(membership)))
      .filter((item): item is ProtoLibraryItem => Boolean(item));
  }, [activePlaylistId, items, itemsByKey, memberships]);

  const filteredPlaylists = useMemo(() => {
    if (!normalizedSearchQuery) return playlists;
    return playlists.filter(playlist => playlist.title.toLowerCase().includes(normalizedSearchQuery));
  }, [normalizedSearchQuery, playlists]);

  const filteredItems = useMemo(() => {
    return orderedPlaylistItems.filter(item => {
      const matchesSearch = !normalizedSearchQuery || item.title.toLowerCase().includes(normalizedSearchQuery);
      const matchesSource =
        sourceFilter === 'all' ||
        (sourceFilter === 'youtube' && item.source === 'youtube') ||
        (sourceFilter === 'uploaded' && item.source === 'uploaded');
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'unassigned' && !item.status) ||
        item.status === statusFilter;

      return matchesSearch && matchesSource && matchesStatus;
    });
  }, [normalizedSearchQuery, orderedPlaylistItems, sourceFilter, statusFilter]);

  const activePlaylistMemberships = useMemo(() => {
    if (!activePlaylistId) return [];
    return sortMemberships(memberships.filter(membership => membership.playlistId === activePlaylistId));
  }, [activePlaylistId, memberships]);

  const activePlaylistIndexByItem = useMemo(() => {
    const byItem = new Map<string, number>();
    activePlaylistMemberships.forEach((membership, index) => byItem.set(membershipKey(membership), index));
    return byItem;
  }, [activePlaylistMemberships]);

  const canReorderPlaylist = Boolean(
    activePlaylistId &&
    activePlaylistMemberships.length > 1 &&
    !normalizedSearchQuery &&
    sourceFilter === 'all' &&
    statusFilter === 'all'
  );

  const usedUploadSlots = items.filter(consumesUploadSlot).length;
  const libraryLimit = 5;
  const activeFilterCount = Number(sourceFilter !== 'all') + Number(statusFilter !== 'all');
  const selectedItem = pendingDeleteKey ? itemsByKey.get(pendingDeleteKey) || null : null;

  const closeTransientSurfaces = () => {
    setOpenMenuKey(null);
    setOpenPickerKey(null);
    setCreatePlaylistOpen(false);
  };

  const handleSelectSurface = (surface: LibrarySurface) => {
    setActiveSurface(surface);
    setActivePlaylistId(null);
    setSearchQuery('');
    setSearchOpen(false);
    closeTransientSurfaces();
  };

  const handleSelectPlaylist = (playlistId: string) => {
    setActiveSurface('playlists');
    setActivePlaylistId(playlistId);
    setSearchQuery('');
    setSearchOpen(false);
    closeTransientSurfaces();
  };

  const handleCycleStatus = (item: ProtoLibraryItem) => {
    const nextStatus = getNextStatus(item.status);
    const previousStatus = item.status;
    setItems(previous => previous.map(candidate =>
      itemKey(candidate) === itemKey(item) ? { ...candidate, status: nextStatus } : candidate
    ));
    setToast({
      message: `Status set to ${getStatusLabel(nextStatus)}`,
      undo: () => setItems(previous => previous.map(candidate =>
        itemKey(candidate) === itemKey(item) ? { ...candidate, status: previousStatus } : candidate
      ))
    });
  };

  const startRename = (item: ProtoLibraryItem) => {
    setEditingKey(itemKey(item));
    setRenameDraft(item.title);
    setOpenMenuKey(null);
  };

  const saveRename = (item: ProtoLibraryItem) => {
    const cleanTitle = renameDraft.trim();
    if (!cleanTitle) return;
    const oldTitle = item.title;
    setItems(previous => previous.map(candidate =>
      itemKey(candidate) === itemKey(item) ? { ...candidate, title: cleanTitle } : candidate
    ));
    setEditingKey(null);
    setToast({
      message: 'Title updated',
      undo: () => setItems(previous => previous.map(candidate =>
        itemKey(candidate) === itemKey(item) ? { ...candidate, title: oldTitle } : candidate
      ))
    });
  };

  const handleShareToggle = (item: ProtoLibraryItem) => {
    const key = itemKey(item);
    const alreadyShared = Boolean(item.share);
    setItems(previous => previous.map(candidate => {
      const candidateKey = itemKey(candidate);

      if (candidateKey !== key && !alreadyShared) {
        return { ...candidate, share: undefined };
      }

      if (candidateKey === key) {
        return {
          ...candidate,
          share: alreadyShared ? undefined : { accessCount: 0, maxViews: 30, daysRemaining: 7 }
        };
      }

      return candidate;
    }));
    setToast({
      message: alreadyShared
        ? 'Share link revoked'
        : 'Share link created. Other active shares were deactivated.'
    });
  };

  const handleToggleMembership = (item: ProtoLibraryItem, playlistId: string) => {
    const key = itemKey(item);
    const playlist = playlists.find(candidate => candidate.id === playlistId);
    if (!playlist) return;

    const existing = memberships.find(membership =>
      membership.playlistId === playlistId && membershipKey(membership) === key
    );

    if (existing) {
      setMemberships(previous => previous.filter(membership => membership !== existing));
      setToast({
        message: `Removed from ${playlist.title}`,
        undo: () => setMemberships(previous => [...previous, existing])
      });
      return;
    }

    const nextMembership: ProtoMembership = {
      playlistId,
      itemId: item.id,
      itemCollection: item.collection,
      position: memberships.filter(membership => membership.playlistId === playlistId).length
    };
    setMemberships(previous => [...previous, nextMembership]);
    setToast({
      message: `Added to ${playlist.title}`,
      undo: () => setMemberships(previous => previous.filter(membership => membership !== nextMembership))
    });
  };

  const createPlaylist = (title: string) => {
    const cleanTitle = title.trim();
    if (!cleanTitle) return null;

    const existing = playlists.find(playlist => playlist.title.toLowerCase() === cleanTitle.toLowerCase());
    if (existing) return existing;

    const nextPlaylist = {
      id: `playlist-${slugify(cleanTitle)}-${playlists.length + 1}`,
      title: cleanTitle
    };
    setPlaylists(previous => [...previous, nextPlaylist]);
    return nextPlaylist;
  };

  const handleCreatePlaylistForItem = (item: ProtoLibraryItem) => {
    const playlist = createPlaylist(newPlaylistTitle);
    if (!playlist) return;
    handleToggleMembership(item, playlist.id);
    setNewPlaylistTitle('');
    setPlaylistQuery('');
  };

  const handleCreatePlaylistView = () => {
    const playlist = createPlaylist(newPlaylistTitle);
    if (!playlist) return;
    setNewPlaylistTitle('');
    setCreatePlaylistOpen(false);
    handleSelectPlaylist(playlist.id);
  };

  const handleRemoveFromPlaylist = (item: ProtoLibraryItem, playlistId: string) => {
    const existing = memberships.find(membership =>
      membership.playlistId === playlistId && membershipKey(membership) === itemKey(item)
    );
    if (!existing) return;
    const playlist = playlists.find(candidate => candidate.id === playlistId);
    setMemberships(previous => previous.filter(membership => membership !== existing));
    setOpenMenuKey(null);
    setToast({
      message: `Removed from ${playlist?.title || 'playlist'}`,
      undo: () => setMemberships(previous => [...previous, existing])
    });
  };

  const handleDeleteSelectedItem = () => {
    if (!selectedItem) return;
    const deletedItem = selectedItem;
    const deletedMemberships = memberships.filter(membership => membershipKey(membership) === itemKey(deletedItem));
    setItems(previous => previous.filter(item => itemKey(item) !== itemKey(deletedItem)));
    setMemberships(previous => previous.filter(membership => membershipKey(membership) !== itemKey(deletedItem)));
    setPendingDeleteKey(null);
    setOpenMenuKey(null);
    setToast({
      message: 'Video deleted',
      undo: () => {
        setItems(previous => [...previous, deletedItem]);
        setMemberships(previous => [...previous, ...deletedMemberships]);
      }
    });
  };

  const movePlaylistItem = (item: ProtoLibraryItem, direction: -1 | 1) => {
    if (!activePlaylistId) return;
    const currentIndex = activePlaylistIndexByItem.get(itemKey(item));
    if (currentIndex === undefined) return;
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= activePlaylistMemberships.length) return;

    const reordered = [...activePlaylistMemberships];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    const byKey = new Map(reordered.map((membership, index) => [
      `${membership.playlistId}:${membershipKey(membership)}`,
      { ...membership, position: index }
    ]));

    setMemberships(previous => previous.map(membership =>
      membership.playlistId === activePlaylistId
        ? byKey.get(`${membership.playlistId}:${membershipKey(membership)}`) || membership
        : membership
    ));
    setToast({ message: 'Playlist order updated' });
  };

  const renderPlaylistPicker = (item: ProtoLibraryItem) => {
    const normalizedPlaylistQuery = playlistQuery.trim().toLowerCase();
    const matchingPlaylists = playlists.filter(playlist =>
      playlist.title.toLowerCase().includes(normalizedPlaylistQuery)
    );
    const currentPlaylistIds = currentPlaylistIdsByItem.get(itemKey(item)) || [];

    return (
      <div className="library-playlist-picker" role="dialog" aria-label="Manage playlists">
        <div className="library-picker-header">
          <strong>Manage playlists</strong>
          <span>{currentPlaylistIds.length ? `${currentPlaylistIds.length} selected` : 'None selected'}</span>
        </div>

        <label className="library-search-field">
          <Search size={14} aria-hidden="true" />
          <input
            value={playlistQuery}
            onChange={event => setPlaylistQuery(event.target.value)}
            placeholder="Search playlists"
            aria-label="Search playlists to add"
          />
        </label>

        <div className="library-picker-list">
          {matchingPlaylists.length ? matchingPlaylists.map(playlist => {
            const isCurrent = currentPlaylistIds.includes(playlist.id);
            return (
              <button
                key={playlist.id}
                type="button"
                className={`library-picker-row ${isCurrent ? 'is-selected' : ''}`}
                aria-pressed={isCurrent}
                onClick={() => handleToggleMembership(item, playlist.id)}
              >
                <span className="library-checkbox">{isCurrent && <Check size={12} strokeWidth={3} />}</span>
                <FolderOpen size={14} />
                <span>{playlist.title}</span>
                <small>{isCurrent ? 'Remove' : 'Add'}</small>
              </button>
            );
          }) : (
            <p className="library-empty-mini">No playlists found.</p>
          )}
        </div>

        <form
          className="library-create-row"
          onSubmit={event => {
            event.preventDefault();
            handleCreatePlaylistForItem(item);
          }}
        >
          <input
            value={newPlaylistTitle}
            onChange={event => setNewPlaylistTitle(event.target.value)}
            placeholder="New playlist name"
            aria-label="New playlist name"
          />
          <button type="submit" disabled={!newPlaylistTitle.trim()} aria-label="Create and add playlist">
            <Plus size={15} />
          </button>
        </form>
      </div>
    );
  };

  const renderVideoCard = (item: ProtoLibraryItem) => {
    const key = itemKey(item);
    const isYouTube = item.source === 'youtube';
    const shareNearLimit = item.share && (item.share.maxViews - item.share.accessCount <= 5 || item.share.daysRemaining <= 1);
    const playlistItemIndex = activePlaylistIndexByItem.get(key) ?? -1;
    const canMove = canReorderPlaylist && playlistItemIndex >= 0;

    return (
      <article key={key} className="library-video-card">
        <button
          type="button"
          className="library-video-open"
          aria-label={`Load ${item.title}`}
          onClick={() => setToast({ message: `Loaded ${item.title}` })}
        >
          <div className={`library-thumbnail ${item.thumbnailClass}`}>
            {isYouTube && (
              <span className="library-youtube-badge" title="YouTube project - does not use upload storage">
                <Youtube size={19} strokeWidth={2.5} />
              </span>
            )}
            <span className="library-play-overlay">
              <Play size={28} fill="currentColor" />
            </span>
          </div>
        </button>

        <div className="library-card-menu">
          <button
            type="button"
            className="library-icon-button library-menu-button"
            aria-label={`More actions for ${item.title}`}
            aria-expanded={openMenuKey === key}
            onClick={() => {
              setOpenMenuKey(previous => previous === key ? null : key);
              setOpenPickerKey(null);
            }}
          >
            <MoreVertical size={16} />
          </button>

          {openMenuKey === key && (
            <div className="library-menu" role="menu" aria-label={`Actions for ${item.title}`}>
              <button type="button" role="menuitem" onClick={() => handleCycleStatus(item)}>
                Status: {getStatusLabel(item.status)}
              </button>
              <button type="button" role="menuitem" onClick={() => startRename(item)}>
                Rename
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpenMenuKey(null);
                  setOpenPickerKey(key);
                }}
              >
                Manage playlists
              </button>
              {!isYouTube && (
                <button type="button" role="menuitem" onClick={() => handleShareToggle(item)}>
                  {item.share ? 'Revoke share' : 'Share'}
                </button>
              )}
              {activePlaylistId && (
                <button type="button" role="menuitem" onClick={() => handleRemoveFromPlaylist(item, activePlaylistId)}>
                  Remove from playlist
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="is-danger"
                onClick={() => setPendingDeleteKey(key)}
              >
                Delete video
              </button>
            </div>
          )}
        </div>

        <div className="library-card-body">
          {editingKey === key ? (
            <div className="library-rename-block">
              <input
                value={renameDraft}
                onChange={event => setRenameDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') saveRename(item);
                  if (event.key === 'Escape') setEditingKey(null);
                }}
                aria-label={`Rename ${item.title}`}
                autoFocus
              />
              <div className="library-rename-actions">
                <button type="button" onClick={() => saveRename(item)}>
                  <Check size={14} />
                  Save
                </button>
                <button type="button" onClick={() => setEditingKey(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="library-title-row">
              <h3>{item.title}</h3>
              {canMove && (
                <div className="library-reorder-group" role="group" aria-label={`Reorder ${item.title}`}>
                  <GripVertical size={15} aria-label="Drag to reorder" />
                  <button
                    type="button"
                    className="library-small-icon"
                    aria-label={`Move ${item.title} up`}
                    disabled={playlistItemIndex === 0}
                    onClick={() => movePlaylistItem(item, -1)}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    className="library-small-icon"
                    aria-label={`Move ${item.title} down`}
                    disabled={playlistItemIndex === activePlaylistMemberships.length - 1}
                    onClick={() => movePlaylistItem(item, 1)}
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>
              )}
            </div>
          )}

          {item.share && (
            <div className={`library-share-badge ${shareNearLimit ? 'is-near-limit' : ''}`}>
              <Share2 size={12} />
              <span>Shared</span>
              <span className="library-share-chip"><Eye size={10} />{item.share.maxViews - item.share.accessCount}/{item.share.maxViews}</span>
              <span className="library-share-chip"><Calendar size={10} />{item.share.daysRemaining}d</span>
            </div>
          )}

          <div className="library-meta-row">
            <span><Clock size={12} />{item.createdLabel}</span>
            <span>{isYouTube && <Youtube size={12} />}{item.sizeLabel}</span>
            <span className="library-segment-chip">{item.segments} seg</span>
            {item.status && <span className={`library-status-chip is-${item.status}`}>{getStatusIcon(item.status)}{getStatusLabel(item.status)}</span>}
          </div>

          {openPickerKey === key && renderPlaylistPicker(item)}

          {item.share && shareNearLimit && (
            <div className="library-share-warning">
              <AlertCircle size={12} />
              <span>
                {item.share.maxViews - item.share.accessCount <= 5 && `Only ${item.share.maxViews - item.share.accessCount} views left. `}
                {item.share.daysRemaining <= 1 && 'Expires tomorrow.'}
              </span>
            </div>
          )}
        </div>
      </article>
    );
  };

  return (
    <main className="library-prototype">
      <section className="library-prototype-shell" aria-label="Library prototype">
        <div className="library-context">
          <div className="library-context-topbar">
            <strong>Croop</strong>
            <span>Current editor shell preview</span>
          </div>
          <div className="library-context-video">
            <div className="library-context-figure library-context-figure-one" />
            <div className="library-context-figure library-context-figure-two" />
            <div className="library-context-figure library-context-figure-three" />
            <span className="library-context-badge">
              <Play size={14} />
              {filteredItems[0]?.title || 'Library item'}
            </span>
          </div>
          <div className="library-context-controls">
            <button type="button"><ChevronLeft size={16} /></button>
            <button type="button" className="is-primary"><Play size={18} /></button>
            <button type="button"><ChevronLeft className="library-next-icon" size={16} /></button>
            <span>Practice controls stay behind the drawer</span>
          </div>
        </div>

        <aside className="library-drawer" aria-label="Library drawer">
          <header className="library-drawer-header">
            {activePlaylist ? (
              <button
                type="button"
                className="library-icon-button"
                aria-label="Back to Playlists"
                onClick={() => {
                  setActivePlaylistId(null);
                  closeTransientSurfaces();
                }}
              >
                <ChevronLeft size={17} />
              </button>
            ) : (
              <div className="library-header-switcher" role="tablist" aria-label="Library sections">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSurface === 'library'}
                  className={`library-header-tab ${activeSurface === 'library' ? 'is-active' : ''}`}
                  aria-label="Files"
                  title="Files"
                  onClick={() => handleSelectSurface('library')}
                >
                  <LibraryBig size={18} />
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSurface === 'playlists'}
                  className={`library-header-tab ${activeSurface === 'playlists' ? 'is-active' : ''}`}
                  aria-label="Playlists"
                  title="Playlists"
                  onClick={() => handleSelectSurface('playlists')}
                >
                  <ListVideo size={18} />
                </button>
              </div>
            )}

            {activePlaylist && <h2 className="library-drawer-title">{activePlaylist.title}</h2>}

            <div className={`library-search-box ${isSearchExpanded ? 'is-expanded' : ''}`}>
              {!isSearchExpanded ? (
                <button
                  type="button"
                  className="library-icon-button"
                  aria-label={`Open ${isPlaylistCollectionView ? 'playlist' : 'library'} search`}
                  onClick={() => setSearchOpen(true)}
                >
                  <Search size={15} />
                </button>
              ) : (
                <label>
                  <Search size={15} aria-hidden="true" />
                  <input
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Escape') {
                        setSearchOpen(false);
                        setSearchQuery('');
                      }
                    }}
                    placeholder={isPlaylistCollectionView ? 'Search playlists' : 'Search videos'}
                    aria-label={isPlaylistCollectionView ? 'Search playlists' : 'Search library'}
                    autoFocus
                  />
                  <button
                    type="button"
                    aria-label="Close search"
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <X size={14} />
                  </button>
                </label>
              )}
            </div>

            <button type="button" className="library-close-button" aria-label="Close library">
              <X size={18} />
            </button>
          </header>

          <section className="library-controls">
            {isPlaylistCollectionView && (
              <div className="library-playlist-toolbar">
                {createPlaylistOpen ? (
                  <form
                    className="library-create-playlist-form"
                    onSubmit={event => {
                      event.preventDefault();
                      handleCreatePlaylistView();
                    }}
                  >
                    <input
                      value={newPlaylistTitle}
                      onChange={event => setNewPlaylistTitle(event.target.value)}
                      placeholder="New playlist name"
                      aria-label="New playlist name"
                    />
                    <button type="submit" disabled={!newPlaylistTitle.trim()} aria-label="Create playlist">
                      <Check size={14} />
                    </button>
                    <button type="button" aria-label="Cancel playlist creation" onClick={() => setCreatePlaylistOpen(false)}>
                      <X size={14} />
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="library-icon-button"
                    aria-label="Create playlist"
                    onClick={() => setCreatePlaylistOpen(true)}
                  >
                    <Plus size={15} />
                  </button>
                )}
              </div>
            )}

            {activePlaylist && (
              <button type="button" className="library-breadcrumb" onClick={() => setActivePlaylistId(null)}>
                <FolderOpen size={14} />
                <span>Playlists</span>
                <span>/</span>
                <strong>{activePlaylist.title}</strong>
              </button>
            )}

            {!isPlaylistCollectionView && (
              <div className="library-filter-shell">
                <button
                  type="button"
                  className={`library-filter-toggle ${activeFilterCount ? 'has-filters' : ''}`}
                  aria-label="Toggle library filters"
                  aria-expanded={filtersOpen}
                  onClick={() => setFiltersOpen(previous => !previous)}
                >
                  <SlidersHorizontal size={13} />
                </button>

                {filtersOpen && (
                  <div className="library-filter-panel">
                    <div role="group" aria-label="Library source filter" className="library-source-filters">
                      {(['youtube', 'uploaded'] as SourceFilter[]).map(filter => (
                        <button
                          key={filter}
                          type="button"
                          className={sourceFilter === filter ? 'is-active' : ''}
                          aria-pressed={sourceFilter === filter}
                          onClick={() => setSourceFilter(previous => previous === filter ? 'all' : filter)}
                        >
                          {filter === 'youtube' ? 'YTube' : 'Uploaded'}
                        </button>
                      ))}
                    </div>

                    <div role="group" aria-label="Library learning status filter" className="library-status-filters">
                      {(['queued', 'learning', 'learned'] as LearningStatus[]).map(status => (
                        <button
                          key={status}
                          type="button"
                          title={status}
                          aria-label={`Show ${status} library items`}
                          aria-pressed={statusFilter === status}
                          className={statusFilter === status ? `is-active is-${status}` : ''}
                          onClick={() => setStatusFilter(previous => previous === status ? 'all' : status)}
                        >
                          {getStatusIcon(status)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="library-list">
            {isPlaylistCollectionView ? (
              <div className="library-playlist-list">
                {filteredPlaylists.length ? filteredPlaylists.map(playlist => {
                  const cover = playlistCoverItems.get(playlist.id);
                  const count = memberships.filter(membership => membership.playlistId === playlist.id).length;

                  return (
                    <button
                      key={playlist.id}
                      type="button"
                      className="library-playlist-card"
                      aria-label={`Show playlist ${playlist.title}`}
                      onClick={() => handleSelectPlaylist(playlist.id)}
                    >
                      {cover ? (
                        <span className={`library-playlist-cover library-thumbnail ${cover.thumbnailClass}`}>
                          {cover.source === 'youtube' && <Youtube size={18} />}
                        </span>
                      ) : (
                        <span className="library-playlist-cover is-empty">
                          <FolderOpen size={22} />
                        </span>
                      )}
                      <span className="library-playlist-copy">
                        <strong>{playlist.title}</strong>
                        <small>{count} items</small>
                        {!cover && <small>No items yet</small>}
                      </span>
                    </button>
                  );
                }) : (
                  <div className="library-empty-state">
                    <FolderOpen size={42} />
                    <strong>{searchQuery.trim() ? 'No matching playlists' : 'No playlists yet'}</strong>
                  </div>
                )}
              </div>
            ) : filteredItems.length ? (
              <div className="library-video-list">
                {filteredItems.map(renderVideoCard)}
              </div>
            ) : (
              <div className="library-empty-state">
                <Video size={42} />
                <strong>{activePlaylist ? 'No videos in this playlist' : 'No matching videos'}</strong>
                {(normalizedSearchQuery || activeFilterCount > 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchOpen(false);
                      setSourceFilter('all');
                      setStatusFilter('all');
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </section>

          <footer className="library-account-footer">
            <div className="library-user-row">
              <span className="library-avatar"><User size={15} /></span>
              <span>
                <strong>Cristina</strong>
                <small>cristina@example.com</small>
              </span>
            </div>
            <div className="library-plan-row">
              <span>Free</span>
              <div className="library-storage">
                <span style={{ width: `${Math.min((usedUploadSlots / libraryLimit) * 100, 100)}%` }} />
              </div>
              <strong>{usedUploadSlots} / {libraryLimit} used</strong>
            </div>
            <button type="button">Upgrade</button>
          </footer>
        </aside>

        {toast && (
          <div className="library-toast" role="status" aria-live="polite">
            <span>{toast.message}</span>
            {toast.undo && <button type="button" onClick={toast.undo}>Undo</button>}
            <button type="button" aria-label="Dismiss toast" onClick={() => setToast(null)}>
              <X size={13} />
            </button>
          </div>
        )}

        {selectedItem && (
          <div className="library-confirm-overlay" role="dialog" aria-label="Delete video confirmation">
            <div className="library-confirm-box">
              <Trash2 size={22} />
              <h3>Delete video?</h3>
              <p>{selectedItem.title} will be removed from the library and from every playlist.</p>
              <div>
                <button type="button" onClick={() => setPendingDeleteKey(null)}>Cancel</button>
                <button type="button" className="is-danger" onClick={handleDeleteSelectedItem}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default LibraryPrototype;
